import { useState, useCallback } from "react";
import { Col, Row, Card, CardTitle, Button, CardSubtitle } from "reactstrap";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CRow, CCol, CForm, CFormLabel } from "@coreui/react";
import {
  AddNewClass,
  DeleteClass,
  AddTeachersToClass,
  HandleAddNewTeacher,
  SaveProgramFocusControlSetting,
} from "../../actions/teacherMutations";
import { FormattedMessage, useIntl } from "react-intl";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import CreateClassInProgram from "../Modal/CreateClassInProgram";
import ConfirmDialog from "../Modal/ConfirmDialog";
import DataGrid from "../DataGrids/DataGrid";
import ImportStudentsToClassModal from "../Modal/ImportStudentsToClassModal";
import FocusControl from "../Teacher/FocusControl";
import useFocusControl from "./hooks/useFocusControl";

export default function ProgramAdministration(props) {
  const [promptClassRemoval, setPromptClassRemoval] = useState(false);
  const [classForRemoval, setClassForRemoval] = useState(null);
  const [addClassModalOpen, setAddClassModalOpen] = useState(false);
  const [importStudentsModalOpen, setImportStudentsModalOpen] = useState(false);
  const [createdClassId, setCreatedClassId] = useState(null);
  const [
    importStudentsAfterCreatingClass,
    setImportStudentsAfterCreatingClass,
  ] = useState(false);
  const [newTeachersToAdd, setNewTeachersToAdd] = useState([]);

  const addClassMutation = useMutation({ mutationFn: AddNewClass });
  const deleteClassMutation = useMutation({ mutationFn: DeleteClass });
  const handleAddTeachersToClass = useMutation({ mutationFn: AddTeachersToClass });
  const mutateAddNewTeacher = useMutation({ mutationFn: HandleAddNewTeacher });
  const queryClient = useQueryClient();
  const intl = useIntl();

  const buildDeviantData = useCallback((fcSettings) => {
    const moduleItem = fcSettings.find((x) => x.programSetting?.target === 0);
    const finalItem = fcSettings.find((x) => x.programSetting?.target === 1);

    const allDeviantClasses = [
      ...(moduleItem?.deviantClassSettings || []),
      ...(finalItem?.deviantClassSettings || []),
    ];
    const uniqueClassIds = [
      ...new Set(allDeviantClasses.map((c) => c.classId)),
    ];
    const deviantClasses = uniqueClassIds.map((classId) => {
      const cls = allDeviantClasses.find((c) => c.classId === classId);
      return { classId, className: cls?.className };
    });

    const allDeviantStudents = [
      ...(moduleItem?.deviantStudentSettings || []),
      ...(finalItem?.deviantStudentSettings || []),
    ];
    const uniqueStudentIds = [
      ...new Set(allDeviantStudents.map((s) => s.studentId)),
    ];
    const deviantStudents = uniqueStudentIds.map((studentId) => {
      const student = allDeviantStudents.find((s) => s.studentId === studentId);
      return {
        studentId,
        studentName: student?.studentName || null,
        className: student?.className || null,
      };
    });

    return { deviantClasses, deviantStudents };
  }, []);

  const onRefreshSettings = useCallback(async () => {
    const programId = String(props.programInfo?.data?.id);
    await queryClient.refetchQueries({ queryKey: ["FetchProgramById", programId] });
    const updatedProgram = queryClient.getQueryData([
      "FetchProgramById",
      programId,
    ]);
    return updatedProgram?.focusControlSettings || [];
  }, [queryClient, props.programInfo?.data?.id]);

  const fc = useFocusControl({
    settingKey: "programSetting",
    entityId: props.programInfo.data.id,
    entityIdKey: "programId",
    focusControlSettings: props.programInfo?.data?.focusControlSettings,
    saveFn: SaveProgramFocusControlSetting,
    onRefreshSettings,
    buildDeviantData,
  });

  const promptRemoveClass = (value) => {
    setClassForRemoval(value);
    setPromptClassRemoval(true);
  };

  const hideImportStudentsModal = () => {
    setImportStudentsModalOpen(false);
  };

  const deleteClassColumn = (value) => {
    return (
      <Button
        color="primary"
        className="p-1"
        outline
        disabled={!props.teacherInfo.fullAccess}
        onClick={() => promptRemoveClass(value)}
      >
        <FormattedMessage id="DeleteClass" />
      </Button>
    );
  };

  const classesTableDataSource = [...props.programInfo.data.classes]
    .sort((a, b) => a.name.localeCompare(b.name, "sv"))
    .map((cl) => ({ uniqueId: cl.id, name: cl.name, edit: deleteClassColumn(cl) }));

  const toggleAddClassModal = () => {
    setImportStudentsAfterCreatingClass(false);
    setAddClassModalOpen((prevState) => !prevState);
  };

  const closeAddClassModal = () => {
    setImportStudentsAfterCreatingClass(false);
    setAddClassModalOpen(false);
  };

  const toggleAddClassAndImportModal = () => {
    setImportStudentsAfterCreatingClass(true);
    setAddClassModalOpen((prevState) => !prevState);
  };

  const classesTableColumns = [
    {
      id: "name",
      key: "name",
      header: () => <FormattedMessage id="Name" />,
      width: 150,
      sortingFn: (a, b, columnId) =>
        a.getValue(columnId).localeCompare(b.getValue(columnId)),
    },
    {
      id: "edit",
      key: "edit",
      header: () => <FormattedMessage id="Edit" />,
      width: 150,
      sortable: false,
    },
  ];

  const reloadProgramAndTeacherData = () => {
    if (importStudentsModalOpen) {
      setImportStudentsModalOpen(false);
    }
    queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
    queryClient.invalidateQueries({ queryKey: ["FetchProgramById"] });
  };

  const handleDeleteClass = () => {
    deleteClassMutation.mutate(
      {
        id: classForRemoval.id,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            setPromptClassRemoval(false);
            queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
            queryClient.invalidateQueries({ queryKey: ["FetchProgramById"] });
            toast.success(<FormattedMessage id="ClassDeleted" />);
          } else {
            setPromptClassRemoval(false);
            toast.warn(
              typeof data === "string" ? formatServiceError(intl, data) : data
            );
          }
        },
      }
    );
  };

  const handleAddedClass = async (cl) => {
    var classToAdd = {
      name: cl.name,
      school: props.programInfo.data.customer,
      program: props.programInfo.data,
      orientation: cl.orientation,
    };
    addClassMutation.mutate(
      {
        cl: classToAdd,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            setCreatedClassId(data.data.result.id);
            if (newTeachersToAdd.length > 0) {
              addNewTeachersToClass(newTeachersToAdd, data.data.result.id);
            }
            if (cl.teachers.length > 0) {
              addTeachersToClass(cl.teachers, data.data.result.id);
            } else {
              toggleAddClassModal();
              reloadProgramAndTeacherData();
              toast.success(<FormattedMessage id="ClassUpdated" />);
            }
            if (importStudentsAfterCreatingClass) {
              setImportStudentsModalOpen(true);
            }
          } else {
            toggleAddClassModal();
            toast.warn(data);
          }
        },
        throwOnError: true,
        onError: (data) => {
          toggleAddClassModal();
          toast.warn(data.response.data);
        },
      }
    );
  };

  const addNewTeachersToClass = (teachers, classId) => {
    teachers.forEach((teacher) => {
      mutateAddNewTeacher.mutate(
        {
          name: teacher.name,
          email: teacher.email,
          fullAccess: teacher.fullAccess,
          purchaser: teacher.purchaser,
        },
        {
          onSuccess: (data) => {
            if (data?.status > 199 && data.status < 300) {
              let teacherIdsToAdd = [];
              teacherIdsToAdd.push(data.data.result.id);
              addTeachersToClass(teacherIdsToAdd, classId);
            } else {
              toast.warn(data);
            }
          },
        }
      );
    });
  };

  const addTeachersToClass = (teacherIds, classId) => {
    handleAddTeachersToClass.mutate(
      {
        teacherIds: teacherIds,
        classId: classId,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            closeAddClassModal();
            reloadProgramAndTeacherData();
            toast.success(<FormattedMessage id="ClassUpdated" />);
          } else {
            toggleAddClassModal();
            toast.warn(data);
          }
        },
        throwOnError: true,
        onError: (data) => {
          closeAddClassModal();
          toast.warn(data.response.data);
        },
      }
    );
  };

  return (
    <div className="animated fadeIn">
      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="ProgramInformation" />
            <hr />
          </CardTitle>
          <CRow>
            <CCol sm="6">
              <CForm>
                <CFormLabel htmlFor="programName">
                  <FormattedMessage id="Program" />:
                </CFormLabel>
                <span className="ms-2">{props.programInfo.data.name}</span>
              </CForm>
            </CCol>
          </CRow>
        </Card>
      </Col>

      <FocusControl
        targetSettings={fc.targetSettings}
        deviantClasses={fc.deviantClasses}
        deviantStudents={fc.deviantStudents}
        deviationSource="Program"
        onSaveTarget={fc.handleSaveFocusTarget}
        savingTarget={fc.savingTarget}
      />

      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="Classes" />
            <hr className="pb-0 mb-0" />
          </CardTitle>
          <CardSubtitle>
            <Row>
              <Col>
                <Button
                  size="sm"
                  color="primary"
                  outline
                  className="my-1 m-y-md-0 me-md-1"
                  disabled={!props.teacherInfo.fullAccess}
                  onClick={toggleAddClassModal}
                >
                  <FormattedMessage id="CreateNewClass" />
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  outline
                  disabled={!props.teacherInfo.fullAccess}
                  className="my-1 m-y-md-0 mx-md-1"
                  onClick={toggleAddClassAndImportModal}
                >
                  <FormattedMessage id="CreateAndImport" />
                </Button>
              </Col>
            </Row>
            <DataGrid
              columns={classesTableColumns}
              rows={classesTableDataSource}
            />
          </CardSubtitle>
        </Card>
      </Col>
      <ConfirmDialog
        isOpen={promptClassRemoval}
        toggle={() => setPromptClassRemoval(!promptClassRemoval)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          classForRemoval ? (
            <FormattedMessage
              values={{ classname: classForRemoval.name }}
              id="SureYouWantToDeleteClass"
            />
          ) : (
            ""
          )
        }
        onProceed={handleDeleteClass}
        danger={true}
      />
      <CreateClassInProgram
        isOpen={addClassModalOpen}
        toggle={toggleAddClassModal}
        teachers={props.schoolInfo.data.teachers}
        school={props.school}
        program={props.programInfo.data}
        orientations={props.orientations}
        reloadData={reloadProgramAndTeacherData}
        fullAccess={props.teacherInfo.fullAccess}
        purchaser={props.teacherInfo.purchaser}
        handleAddedClass={handleAddedClass}
        addStudentsAfterCreatingClass={importStudentsAfterCreatingClass}
        newTeachersToAdd={newTeachersToAdd}
        setNewTeachersToAdd={setNewTeachersToAdd}
      />
      <ImportStudentsToClassModal
        preSelectedClass={true}
        preSelectedClassId={createdClassId}
        reloadClass={reloadProgramAndTeacherData}
        handleHide={hideImportStudentsModal}
        studentClasses={[]}
        isOpen={importStudentsModalOpen}
      />
    </div>
  );
}
